import dayjs from "dayjs";
import type { IntlShape } from "react-intl";
import {
  z,
  coerce,
  date,
  discriminatedUnion,
  nativeEnum,
  number,
  object,
} from "zod";

import { CabinClass, TripType } from "@/features/flights/types/types";
import { airportSchema, citySchema } from "@/utils/locations";

export const flightFormLocationSchema = discriminatedUnion("type", [
  airportSchema,
  citySchema,
]);

export const flightSearchFormInputSchema = object({
  tripType: nativeEnum(TripType),
  from: flightFormLocationSchema.nullable(),
  to: flightFormLocationSchema.nullable(),
  dates: object({
    departure: coerce.date(),
    return: coerce.date().optional(),
  }),
  adult: number().min(1).max(9),
  child: number().min(0).max(9),
  infantWithoutSeat: number().min(0).max(9),
  cabinClass: nativeEnum(CabinClass),
});
export type FlightSearchFormInputValues = z.infer<
  typeof flightSearchFormInputSchema
>;

export const getFlightSearchFormSchema = (intl: IntlShape) =>
  object({
    dates: object({
      departure: date({
        required_error: intl.formatMessage({
          id: "flights.validation.dates.departure",
          defaultMessage: "Please select departure date",
        }),
      }).refine(
        (date) => {
          if (!date) return true;
          return !dayjs(date).endOf("day").isBefore(dayjs(), "day");
        },
        intl.formatMessage({
          id: "flights.validation.dates.minDeparture",
          defaultMessage: "Departure date cannot be set before today",
        }),
      ),
      return: date().optional(),
    }),
    from: flightFormLocationSchema.nullable().refine(
      (value) => value !== null,
      intl.formatMessage({
        id: "flights.validation.from",
        defaultMessage: "Please select departure airport",
      }),
    ),
    to: flightFormLocationSchema.nullable().refine(
      (value) => value !== null,
      intl.formatMessage({
        id: "flights.validation.to",
        defaultMessage: "Please select destination airport",
      }),
    ),
    tripType: nativeEnum(TripType, {
      required_error: intl.formatMessage({
        id: "flights.validation.tripType",
        defaultMessage: "Please select trip type",
      }),
    }),
    cabinClass: nativeEnum(CabinClass, {
      required_error: intl.formatMessage({
        id: "flights.validation.cabinClass",
        defaultMessage: "Please select cabin class",
      }),
    }),
    adult: number().min(1).max(9),
    child: number().min(0).max(9),
    infantWithoutSeat: number().min(0).max(9),
  }).superRefine((values, ctx) => {
    const { dates, tripType } = values;
    const { departure: departureDate, return: returnDate } = dates;

    if (tripType === TripType.ROUND_TRIP && !returnDate) {
      ctx.addIssue({
        code: "custom",
        path: ["dates", "return"],
        message: intl.formatMessage({
          id: "flights.validation.dates.return",
          defaultMessage: "Please select return date",
        }),
      });
    }

    if (returnDate && departureDate && returnDate < departureDate) {
      ctx.addIssue({
        code: "custom",
        path: ["dates", "return"],
        message: intl.formatMessage({
          id: "flights.validation.dates.minReturn",
          defaultMessage: "Return date cannot be set before departure",
        }),
      });
    }
  });

export type FlightSearchFormValues = z.infer<
  ReturnType<typeof getFlightSearchFormSchema>
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typeCheck: z.input<ReturnType<typeof getFlightSearchFormSchema>> =
  {} as FlightSearchFormInputValues;
